import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AsyncBoundary, ConfirmDialog, Drawer, EmptyState, Icon, type IconName, useToast } from '@kollez/design-system';
import {
  useApplications,
  useApplicationTimeline,
  useUpdateApplication,
  useRunSummaries,
  useNotifCountsBySubject,
  useMarkNotifReadBySubject,
  type ApplicationListItem,
  type ApplicationsListOpts,
} from '@/lib/queries';

/**
 * /applications — Student's applications list.
 *
 * Data model (post Pass 4 + Pass 10):
 *   - GET /applications with page/limit/q/status/sortBy/sortDir/matchMin — server
 *     returns {applications, summary.byStatus} + {page,limit,total}.
 *   - GET /applications/:id/timeline — real event feed (created + statusHistory +
 *     interviews + offers + badges + workflowRun stages + assessment attempts).
 *   - PATCH /applications/:id — status transitions (Withdrawn).
 *
 * Fabrications removed vs the old page:
 *   - Fake ROUNDS[] timeline (derived from status regex) → real event feed
 *   - Client-side active/closed counts from a partial page → server summary
 *   - "12 drives are open" hardcoded hint → live count from summary
 *   - "Cut-off was around the aptitude bar" hardcoded rejection copy → dropped
 *   - Withdraw toast-only → real PATCH mutation
 */

const ROUND_LABEL: Record<string, string> = {
  Applied: 'Applied',
  Under_Review: 'Under review',
  Shortlisted: 'Shortlisted',
  Assessment_Pending: 'Test pending',
  Assessment_Submitted: 'Test submitted',
  Interview_Scheduled: 'Interview',
  Interview_Completed: 'Interview done',
  Offered: 'Offer',
  Accepted: 'Accepted',
  Rejected: 'Rejected',
  Withdrawn: 'Withdrawn',
  On_Hold: 'On hold',
};

const CLOSED_STATUSES = new Set(['Rejected', 'Withdrawn']);

function stageIndex(status: string): number {
  const s = status.toLowerCase();
  if (/accept/.test(s) || /offer/.test(s)) return 4;
  if (/interview/.test(s)) return 3;
  if (/assess|test/.test(s)) return 2;
  if (/short/.test(s)) return 1;
  return 0;
}

function state(status: string): 'win' | 'bad' | 'wd' | 'on' {
  if (/accept|offer/i.test(status)) return 'win';
  if (/withdraw/i.test(status)) return 'wd';
  if (/reject/i.test(status)) return 'bad';
  return 'on';
}

function initials(name?: string): string {
  return (name ?? '?').trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 3).toUpperCase();
}

type Seg = 'active' | 'closed';
type Sort = ApplicationsListOpts['sortBy'];

const PAGE_SIZE = 25;

export function StudentApplicationsPage() {
  const toast = useToast();

  const [seg, setSeg] = useState<Seg>('active');
  const [q, setQ] = useState('');
  const [highMatchOnly, setHighMatchOnly] = useState(false);
  const [sortBy, setSortBy] = useState<Sort>('appliedAt');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmWd, setConfirmWd] = useState(false);

  // Server-side status filter — a comma-list isn't supported yet, so for 'closed' we
  // fetch Rejected and Withdrawn separately in one page each. Simpler: fetch WITHOUT
  // status, use summary to drive counts, and split the visible rows client-side by
  // segment. Since the list is paginated on server, this keeps the network shape
  // simple while still using real server totals.
  const listQuery = useApplications({
    page,
    limit: PAGE_SIZE,
    q: q.trim() || undefined,
    sortBy,
    sortDir: 'desc',
    matchMin: highMatchOnly ? 70 : undefined,
  });
  const applications = listQuery.data?.applications ?? [];
  const summary = listQuery.data?.summary?.byStatus ?? {};

  const activeCount = Object.entries(summary).reduce(
    (n, [k, v]) => n + (CLOSED_STATUSES.has(k) ? 0 : v),
    0,
  );
  const closedCount = Object.entries(summary).reduce(
    (n, [k, v]) => n + (CLOSED_STATUSES.has(k) ? v : 0),
    0,
  );
  const totalCount = activeCount + closedCount;
  const offerCount = (summary.Offered ?? 0) + (summary.Accepted ?? 0);
  const interviewCount = summary.Interview_Scheduled ?? 0;

  const rowsForSeg = useMemo(
    () => applications.filter((a) => (seg === 'active' ? !CLOSED_STATUSES.has(a.status) : CLOSED_STATUSES.has(a.status))),
    [applications, seg],
  );

  // Supplementary overlays kept from prior page — real APIs.
  const runSummaries = useRunSummaries(rowsForSeg.map((r) => r._id));
  const runsById = runSummaries.data?.runs ?? {};
  const notifCounts = useNotifCountsBySubject('Application', rowsForSeg.map((r) => r._id));
  const unreadById = notifCounts.data?.counts ?? {};
  const markRead = useMarkNotifReadBySubject();
  const updateApp = useUpdateApplication();

  const openRow = openId ? applications.find((a) => a._id === openId) : null;

  function openApp(row: ApplicationListItem) {
    setOpenId(row._id);
    if ((unreadById[row._id] ?? 0) > 0) {
      void markRead.mutateAsync({ kind: 'Application', id: row._id }).catch(() => { /* non-fatal */ });
    }
  }

  return (
    <div className="stack">
      <style>{`
        .ap { display: grid; grid-template-columns: 40px minmax(0,1fr) auto; gap: 12px; align-items: center; padding: 14px 16px; border-bottom: 1px solid rgb(var(--line-tertiary)); cursor: pointer; }
        .ap:last-child { border-bottom: 0; }
        .ap:hover { background: rgb(var(--bg-secondary)); }
        .steps { display: flex; align-items: center; gap: 0; margin-top: 8px; }
        .steps span { flex: 1; height: 3px; background: rgb(var(--bg-tertiary)); position: relative; }
        .steps span:not(:last-child) { margin-right: 3px; }
        .steps span.on { background: rgb(var(--ink-accent)); }
        .steps span.win { background: rgb(var(--ink-success)); }
        .steps span.bad { background: rgb(var(--ink-danger)); }
        .filters-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">My applications</h1>
          <p className="subhead">
            {totalCount} this season · {offerCount} offer{offerCount === 1 ? '' : 's'} · {interviewCount} interview{interviewCount === 1 ? '' : 's'} coming up
          </p>
        </div>
      </div>

      {/* Filters row — segmented + search + sort + high-match toggle. Every control
          maps 1:1 to a Pass 4 query param. */}
      <div className="filters-row">
        <div className="segmented">
          <button aria-pressed={seg === 'active'} onClick={() => setSeg('active')}>
            Active <span className="helper ml-1">{activeCount}</span>
          </button>
          <button aria-pressed={seg === 'closed'} onClick={() => setSeg('closed')}>
            Closed <span className="helper ml-1">{closedCount}</span>
          </button>
        </div>
        <input
          type="search"
          className="input"
          placeholder="Search drive or role"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          style={{ minWidth: 200 }}
        />
        <select
          className="select"
          value={sortBy}
          onChange={(e) => { setSortBy(e.target.value as Sort); setPage(1); }}
        >
          <option value="appliedAt">Most recent</option>
          <option value="matchScore.overall">Best match</option>
          <option value="status">By status</option>
          <option value="updatedAt">Recently updated</option>
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={highMatchOnly}
            onChange={(e) => { setHighMatchOnly(e.target.checked); setPage(1); }}
          />
          High match only (≥70)
        </label>
      </div>

      <AsyncBoundary
        kind="table"
        loading={listQuery.isLoading}
        error={listQuery.isError ? (listQuery.error instanceof Error ? listQuery.error.message : true) : undefined}
        empty={!listQuery.isLoading && rowsForSeg.length === 0}
        emptyTitle={seg === 'active' ? 'No active applications' : 'Nothing closed'}
        emptyHint={seg === 'active' ? 'Explore drives to apply — takes one tap and uses your default resume.' : undefined}
        emptyAction={seg === 'active' ? <Link className="btn btn-primary" to="/explore">Explore drives</Link> : undefined}
        onRetry={() => void listQuery.refetch()}
      >
        <div className="card">
          {rowsForSeg.map((row) => {
            const reached = stageIndex(row.status);
            const st = state(row.status);
            const label = ROUND_LABEL[row.status] ?? row.status.replace(/_/g, ' ');
            const badge =
              st === 'win' ? <span className="badge badge-success">Offer</span>
              : st === 'bad' ? <span className="badge">Rejected</span>
              : st === 'wd' ? <span className="badge">Withdrawn</span>
              : <span className="badge badge-info">{label}</span>;
            const driveName = row.drive?.driveName ?? 'Drive';
            const match = row.matchScore?.overall;
            return (
              <div
                key={row._id}
                role="button"
                tabIndex={0}
                className="ap"
                onClick={() => openApp(row)}
                onKeyDown={(e) => { if (e.key === 'Enter') openApp(row); }}
              >
                <span className="logo-tile" style={{ position: 'relative' }}>
                  {initials(driveName)}
                  {unreadById[row._id] ? (
                    <span
                      aria-label={`${unreadById[row._id]} unread`}
                      style={{
                        position: 'absolute', top: -4, right: -4, minWidth: 16, height: 16,
                        padding: '0 4px', borderRadius: 8, background: 'rgb(var(--ink-danger))',
                        color: 'rgb(var(--ink-inverse))', fontSize: 10, fontWeight: 700,
                        lineHeight: '16px', textAlign: 'center',
                      }}
                    >
                      {unreadById[row._id]! > 9 ? '9+' : unreadById[row._id]}
                    </span>
                  ) : null}
                </span>
                <div className="min-w-0">
                  <div className="cell-title truncate">{driveName}</div>
                  <div className="cell-sub truncate">
                    {label}
                    {typeof match === 'number' ? ` · match ${match}` : ''}
                    {runsById[row._id] ? (
                      <> · <span className="tag" title={`Workflow: ${runsById[row._id]!.workflowName}`}>
                        {runsById[row._id]!.currentStage.replace(/_/g, ' ')}
                      </span></>
                    ) : null}
                  </div>
                  <div className="steps" role="img" aria-label={`Reached ${label}`}>
                    {[0, 1, 2, 3, 4].map((i) => {
                      const cls =
                        i <= reached
                          ? (st === 'win' ? 'win' : st === 'bad' && i === reached ? 'bad' : st === 'wd' ? '' : 'on')
                          : '';
                      return <span key={i} className={cls} />;
                    })}
                  </div>
                </div>
                {badge}
              </div>
            );
          })}
          {listQuery.data && rowsForSeg.length === 0 && (
            <EmptyState
              title={seg === 'active' ? 'No active applications on this page' : 'Nothing closed on this page'}
              hint={seg === 'active' ? 'Try the Closed segment or explore new drives.' : undefined}
            />
          )}
        </div>
      </AsyncBoundary>

      {/* Pagination footer — server drives the total count in summary. */}
      {(totalCount > PAGE_SIZE) && (
        <div className="flex items-center justify-between gap-2 mt-2">
          <span className="helper">Page {page}</span>
          <div className="flex gap-2">
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </button>
            <button
              className="btn btn-ghost btn-sm"
              disabled={applications.length < PAGE_SIZE}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      <p className="helper">Results usually come within 5 days of each round. We message you on WhatsApp the moment anything changes.</p>

      {/* Drawer — real timeline from /applications/:id/timeline. */}
      <Drawer open={Boolean(openRow)} onClose={() => setOpenId(null)} title={openRow?.drive?.driveName ?? 'Application'} size="md">
        {openRow ? (
          <ApplicationDrawer
            row={openRow}
            onWithdraw={() => setConfirmWd(true)}
            currentStatusLabel={ROUND_LABEL[openRow.status] ?? openRow.status.replace(/_/g, ' ')}
          />
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={confirmWd}
        onOpenChange={setConfirmWd}
        title={openRow ? `Withdraw from ${openRow.drive?.driveName ?? 'drive'}?` : 'Withdraw?'}
        description="You can't apply to this drive again, and the placement office is told."
        confirmText={updateApp.isPending ? 'Withdrawing…' : 'Withdraw'}
        tone="danger"
        onConfirm={() => {
          if (!openRow) return;
          const targetId = openRow._id;
          const driveName = openRow.drive?.driveName ?? 'drive';
          void updateApp.mutateAsync({ id: targetId, status: 'Withdrawn' })
            .then(() => {
              toast.show({ message: 'Withdrawn from ' + driveName });
              setOpenId(null);
            })
            .catch((e: unknown) => {
              toast.show({ message: e instanceof Error ? e.message : 'Withdraw failed', tone: 'danger' });
            });
        }}
      />
    </div>
  );
}

function ApplicationDrawer({
  row,
  onWithdraw,
  currentStatusLabel,
}: {
  row: ApplicationListItem & { matchScore?: { overall?: number } };
  onWithdraw: () => void;
  currentStatusLabel: string;
}) {
  const timeline = useApplicationTimeline(row._id);
  const st = state(row.status);
  const driveName = row.drive?.driveName ?? 'Drive';

  return (
    <>
      <div className="flex items-center gap-3">
        <span className="logo-tile">{initials(driveName)}</span>
        <div>
          <h2 className="h-section">{driveName}</h2>
          <p className="helper">
            {currentStatusLabel}
            {typeof row.matchScore?.overall === 'number' ? ` · match ${row.matchScore.overall}` : ''}
          </p>
        </div>
      </div>

      <div className="mt-4">
        <h3 className="h-section mb-2">Timeline</h3>
        <AsyncBoundary
          kind="detail"
          loading={timeline.isLoading}
          error={timeline.isError ? (timeline.error instanceof Error ? timeline.error.message : true) : undefined}
          empty={!timeline.isLoading && (timeline.data?.events?.length ?? 0) === 0}
          emptyTitle="No events yet"
          emptyHint="Events land here as you move through the drive."
          onRetry={() => void timeline.refetch()}
        >
          <ol className="timeline">
            {(timeline.data?.events ?? [])
              .slice()
              .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
              .map((ev, i) => (
                <li key={`${ev.kind}-${ev.at}-${i}`} data-state={i === 0 ? 'current' : 'done'}>
                  <span className="t-dot" />
                  <div className="t-title">
                    <TimelineIcon kind={ev.kind} /> {ev.title}
                  </div>
                  <div className="t-meta">
                    {new Date(ev.at).toLocaleString('en-IN', {
                      day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
                    })}
                    {ev.detail ? ` · ${ev.detail}` : ''}
                  </div>
                </li>
              ))}
          </ol>
        </AsyncBoundary>
      </div>

      <dl className="dl mt-4">
        <dt>Resume sent</dt><dd><Link className="link" to="/resume-builder">Primary resume</Link></dd>
        <dt>Placement office</dt><dd>Your TPO · <Link className="link" to="/nexie">Ask Nexie</Link></dd>
      </dl>

      <div className="drawer-foot flex-wrap mt-4">
        {!CLOSED_STATUSES.has(row.status) && st !== 'win' && (
          <button className="btn btn-ghost" onClick={onWithdraw}>Withdraw</button>
        )}
        <Link className="btn btn-outline ml-auto" to={`/applications/${row._id}`}>Open detail</Link>
        {st === 'win' && (
          <Link className="btn btn-primary" to="/offers">See offers</Link>
        )}
      </div>
    </>
  );
}

function TimelineIcon({ kind }: { kind: string }) {
  // Icon registry doesn't have every kind name (no plain 'flag', 'award', etc.);
  // fall back to a small handful that DO exist. Cast is intentional — the runtime
  // set is bounded by the map keys.
  const map: Record<string, IconName> = {
    status: 'chevron-right',
    interview: 'user-check',
    offer: 'file-signature',
    badge: 'calendar-plus',
    workflow: 'chevron-right',
    attempt: 'clipboard-check',
    created: 'chevron-right',
  };
  const name = (map[kind] ?? 'chevron-right') as IconName;
  return <Icon name={name} size={12} />;
}
